
import React, { useEffect, useState } from 'react';
import { Box, TableBody } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  ActionButton,
  DeleteIconWrapper
} from './styles';
import CustomPagination from '../../../components/CustomPagination';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import DesignationModal from './components/DesignationModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import { searchDesignations, deleteDesignation } from '../../../modules/masterdata/actions';
import {
  selectDesignationsList,
  selectSearchDesignationsCount,
  selectCreateDesignationSuccess,
  selectCreateDesignationError,
  selectUpdateDesignationSuccess,
  selectUpdateDesignationError,
  selectDeleteDesignationSuccess,
  selectDeleteDesignationError
} from '../../../modules/masterdata/selectors';
import {
  resetCreateDesignationState,
  resetUpdateDesignationState,
  resetDeleteDesignationState
} from '../../../modules/masterdata/reducers';
import { IDesignation } from '../../../modules/masterdata/types';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../components/common/AlertPopup';

const Designation: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const designations = useSelector(selectDesignationsList);
  const totalCount = useSelector(selectSearchDesignationsCount);

  // Success/Error Selectors
  const createSuccess = useSelector(selectCreateDesignationSuccess);
  const createError = useSelector(selectCreateDesignationError);
  const updateSuccess = useSelector(selectUpdateDesignationSuccess);
  const updateError = useSelector(selectUpdateDesignationError);
  const deleteSuccess = useSelector(selectDeleteDesignationSuccess);
  const deleteError = useSelector(selectDeleteDesignationError);

  const PAGE_SIZE = 10;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState<IDesignation | null>(null);

  // Confirmation Dialog state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Alert state
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  const [page, setPage] = useState(1);

  useEffect(() => {
    setActivePage('designation');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Designation', path: '/master-data/designations' },
    ]);
    setIsShow(true);
    dispatch(searchDesignations({ search: '', page: 1, page_size: PAGE_SIZE }));
  }, [setActivePage, setPath, setIsShow, dispatch]);


  // Handle Notifications and Resets
  useEffect(() => {
    if (createSuccess) {
      setAlert({ message: 'Designation created successfully', severity: 'success' });
      dispatch(searchDesignations({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetCreateDesignationState());
    }
    if (createError) {
      setAlert({ message: createError, severity: 'error' });
      dispatch(resetCreateDesignationState());
    }

    if (updateSuccess) {
      setAlert({ message: 'Designation updated successfully', severity: 'success' });
      dispatch(searchDesignations({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetUpdateDesignationState());
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetUpdateDesignationState());
    }

    if (deleteSuccess) {
      setAlert({ message: 'Designation deleted successfully', severity: 'success' });
      dispatch(searchDesignations({ search: '', page, page_size: PAGE_SIZE }));
      dispatch(resetDeleteDesignationState());
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetDeleteDesignationState());
    }
  }, [createSuccess, createError, updateSuccess, updateError, deleteSuccess, deleteError, dispatch, page]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    dispatch(searchDesignations({ search: '', page: newPage, page_size: PAGE_SIZE }));
  };

  const handleCreateClick = () => {
    setSelectedDesignation(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (designation: IDesignation) => {
    setSelectedDesignation(designation);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await dispatch(deleteDesignation(deleteId));
      setIsConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const totalPages = Math.ceil((totalCount || 0) / PAGE_SIZE);

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Manage Designations</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Designation
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {designations && designations.length > 0 ? (
                designations.map((designation: IDesignation, index: number) => (
                  <StyledTableRow key={designation.designation_id}>
                    <StyledTableCell>{designation.designation_id}</StyledTableCell>
                    <StyledTableCell>{designation.designation_name}</StyledTableCell>
                    <StyledTableCell>{designation.designation_description}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ActionButton onClick={() => handleEditClick(designation)}>
                          <EditIcon fontSize="small" />
                        </ActionButton>
                        <DeleteIconWrapper onClick={() => handleDeleteClick(designation.designation_id)}>
                          <DeleteIcon fontSize="small" />
                        </DeleteIconWrapper>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} align="center">No designations found.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CustomPagination
            totalPages={totalPages > 0 ? totalPages : 1}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContainer>

      <DesignationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        designation={selectedDesignation}
      />

      <ConfirmationDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Designation"
        message="Are you sure you want to delete this designation?"
      />
    </Box>
  );
};

export default Designation;
